import {
  AD_ACCESS_TOKEN_START,
  AD_ACCESS_TOKEN_FAILED,
  AD_ACCESS_TOKEN_SUCCESS,
  AD_LOGIN_SUCCESS,
  AD_LOGIN_FAILED,
  AD_LOGIN_START,
  AD_LOGIN_REDIRECT_SUCCESS,
  AD_LOGIN_REDIRECT_FAILED,
  INITIALIZE_AD_AUTH_SUCCESS,
  INITIALIZE_AD_AUTH_FAILED,
  AD_LOGOUT_START,
  AD_LOGOUT_SUCCESS,
  AD_LOGOUT_FAILED,
  CIAM_LOGIN_STARTED,
  CIAM_LOGIN_SUCCESS,
  CiamLoginResponse
} from './types';
import { Configuration, RedirectRequest } from '@azure/msal-browser';
import { AnyAction, Dispatch } from 'redux';
import { GET_CLAIMS_FAILED, GET_CLAIMS_SUCCESS, SET_USER, getClaims } from '../user-profile';
import { ReduxStore } from '../payload-types';
import { APPS_ROUTES, AUTH_HEADERS, LOGIN_ROUTES } from '@trip-ux/constants';
import { getLoginRequest, getMsalInstance, setLoginRequest, setMsalInstance } from '../auth.service';
import { first, isEmpty } from 'lodash';
import axios from 'axios';

export const initializeCiamLogin =
  () =>
  (dispatch: Dispatch): void => {
    dispatch({ type: CIAM_LOGIN_STARTED });
    window.location.href = LOGIN_ROUTES.CIAM_LOGIN_ROUTE;
  };

export const initializeCiamUser =
  envConfig =>
  async (dispatch: Dispatch, getState: () => ReduxStore): Promise<void> => {
    const { loginPending, tokenPending } = getState().adfsAuth;

    if (!!loginPending && !!tokenPending) {
      return;
    }

    try {
      const { user, claims } = await axios.get<string, CiamLoginResponse>(LOGIN_ROUTES.AUTH_GET_USER);

      dispatch({ type: SET_USER, payload: user });
      dispatch({ type: GET_CLAIMS_SUCCESS, payload: { claims: claims, user: user } });
      dispatch({ type: CIAM_LOGIN_SUCCESS, payload: envConfig });
    } catch (error) {
      dispatch({ type: GET_CLAIMS_FAILED, payload: error });
    }
  };

export const initializeAdAuth =
  (config: Configuration, loginRedirect: RedirectRequest) =>
  (dispatch: Dispatch): void => {
    try {
      setMsalInstance(config);
      setLoginRequest(loginRedirect);
      dispatch({ type: INITIALIZE_AD_AUTH_SUCCESS });
    } catch (error) {
      dispatch({ type: INITIALIZE_AD_AUTH_FAILED, payload: error });
    }
  };

export const adLoginRedirect =
  (clientStorage: Storage) =>
  (dispatch: Dispatch): void => {
    try {
      const msalInstance = getMsalInstance();
      const loginRequest = getLoginRequest();
      const accounts = msalInstance.getAllAccounts();
      if (isEmpty(accounts)) {
        msalInstance.loginRedirect(loginRequest);
      } else {
        delete clientStorage.loginInitiated;
        dispatch(adAccessToken() as unknown as AnyAction);
      }
      dispatch({ type: AD_LOGIN_REDIRECT_SUCCESS });
    } catch (error) {
      dispatch({ type: AD_LOGIN_REDIRECT_FAILED, payload: error });
    }
  };

export const adLoginPromise =
  (clientStorage: Storage) =>
  (dispatch: Dispatch): void => {
    dispatch({ type: AD_LOGIN_START });
    try {
      const msalInstance = getMsalInstance();
      msalInstance
        .handleRedirectPromise()
        .then(response => {
          dispatch(adLoginSuccess(response) as unknown as AnyAction);
          delete clientStorage.loginInitiated;
          dispatch(adAccessToken() as unknown as AnyAction);
        })
        .catch(error => {
          dispatch({
            type: AD_LOGIN_FAILED,
            payload: error
          });
        });
    } catch (error) {
      dispatch({
        type: AD_LOGIN_FAILED,
        payload: error
      });
    }
  };

export const adLogout =
  (clientStorage: Storage) =>
  async (dispatch: Dispatch, getState: () => ReduxStore): Promise<void> => {
    dispatch({ type: AD_LOGOUT_START });
    try {
      dispatch({
        type: AD_LOGOUT_SUCCESS,
        payload: {
          loggout: true
        }
      });
      const { userType } = getState().userProfile;
      clientStorage.clear();
      if (userType === AUTH_HEADERS.LAN_ID) {
        window.history.replaceState(null, '', LOGIN_ROUTES.AZURE_WELCOME_PAGE);
        await axios.get((window.location.href = LOGIN_ROUTES.LOGOUT)).then(() => {
          window.location.href = LOGIN_ROUTES.AZURE_WELCOME_PAGE;
        });
      } else {
        window.history.replaceState(null, '', LOGIN_ROUTES.CIAM_LOGIN_ROUTE);
        await axios
          .post(LOGIN_ROUTES.LOGOUT, null, {})
          .then(() => {
            window.location.href = LOGIN_ROUTES.CIAM_LOGIN_ROUTE;
          })
          .catch(error => {
            // Redirect to CIAM login route to trigger OIDC authentication flow
            window.location.href = LOGIN_ROUTES.CIAM_LOGIN_ROUTE;
            throw error;
          });
      }
    } catch (error) {
      dispatch({
        type: AD_LOGOUT_FAILED,
        payload: error
      });
    }
  };

export const adLoginSuccess =
  (payload: unknown) =>
  (dispatch: Dispatch): void => {
    dispatch({
      type: AD_LOGIN_SUCCESS,
      payload
    });
  };

export const mapAccount = account => ({
  userRef: account.username.substr(0, account.username.indexOf('@')),
  userType: AUTH_HEADERS.LAN_ID,
  name: account.name
});

export const dispatchUserAuthorization = (payload, dispatch, getState) => {
  dispatch({ type: AD_ACCESS_TOKEN_SUCCESS, payload });
  const claimBody = mapAccount(payload.account);
  getClaims({ ...claimBody, userRef: payload.accessToken, idToken: payload.idToken })(dispatch, getState);
};

export const adAccessToken =
  () =>
  (dispatch: Dispatch, getState: () => ReduxStore): Promise<unknown> => {
    dispatch({ type: AD_ACCESS_TOKEN_START });
    const msalInstance = getMsalInstance();
    const config = getLoginRequest();
    const accounts = msalInstance?.getAllAccounts();
    if (accounts && accounts.length > 0) {
      return msalInstance
        .acquireTokenSilent({ ...config, account: first(accounts) })
        .then(payload => {
          dispatchUserAuthorization(payload, dispatch, getState);
        })
        .catch(error => dispatch({ type: AD_ACCESS_TOKEN_FAILED, payload: error }));
    } else {
      return msalInstance
        .acquireTokenPopup(config)
        .then(payload => {
          dispatchUserAuthorization(payload, dispatch, getState);
        })
        .catch(error => dispatch({ type: AD_ACCESS_TOKEN_FAILED, payload: error }));
    }
  };
